import { type NodeViewComponentProps } from "@handlewithcare/react-prosemirror";
import { Logger } from "logging";
import { normalize } from "pathe";
import { forwardRef } from "react";
import { useAsync } from "react-use";

import {
	useAuth,
	useDocumentFromCurrentRoute,
	usePageFromCurrentRoute,
	useRelatedFileContents,
	useWorkspaceFromCurrentRoute,
} from "@moment/state";

import { readAsDataURL } from "../../plugins/imageDropPlugin";

const logger = new Logger("InlineImage");

const isRemoteImageSrc = (src: string) =>
	src.startsWith("http://") ||
	src.startsWith("https://") ||
	src.startsWith("data:") ||
	src.startsWith("blob:");

export const InlineImage = forwardRef<HTMLSpanElement, NodeViewComponentProps>(function InlineImage(
	{ nodeProps, children: _, ...props },
	ref
) {
	const { node } = nodeProps;
	const src = node.attrs["src"] as string;
	const alt = node.attrs["alt"] as string;
	const title = node.attrs["title"] as string | null;

	const { workspaceName } = useWorkspaceFromCurrentRoute();
	const { documentId } = useDocumentFromCurrentRoute();
	const { pageId } = usePageFromCurrentRoute();
	const { callback: getRelatedFileContents } = useRelatedFileContents();
	const { getAccessTokenSilently } = useAuth();

	const needsResolvedSrc = src.length > 0 && !isRemoteImageSrc(src);

	const { value: resolvedSrc } = useAsync(async () => {
		if (!needsResolvedSrc) {
			return src;
		}

		if (!workspaceName || !documentId || !pageId) {
			return src;
		}

		try {
			const token = await getAccessTokenSilently.getAccessTokenSilently?.();
			const fileContents = await getRelatedFileContents({
				workspaceName,
				documentId,
				pageId,
				src,
				token,
			});
			const filename = normalize(src).split("/").pop() ?? src;
			return await readAsDataURL(new Uint8Array(fileContents).buffer, filename);
		} catch (error) {
			logger.error("Failed to load inline image", { cause: error, src });
			return src;
		}
	}, [
		documentId,
		getAccessTokenSilently,
		getRelatedFileContents,
		needsResolvedSrc,
		pageId,
		src,
		workspaceName,
	]);

	return (
		<span ref={ref} {...props} contentEditable={false}>
			<img
				data-inline-image="true"
				src={resolvedSrc ?? src}
				alt={alt}
				title={title ?? undefined}
			/>
		</span>
	);
});
